import React from 'react';
import { Client, Order } from '../types';
import { UserIcon } from './icons';
import { Image } from './Image';

interface ClientListProps {
  clients: Client[];
  onSelectClient: (client: Client) => void;
  searchQuery: string;
  orders?: Order[]; // Ajouté pour les badges
}

export const ClientList: React.FC<ClientListProps> = ({ clients, onSelectClient, searchQuery, orders = [] }) => {
  // Optimisation : Grouper les commandes par client une seule fois
  const ordersByClient = React.useMemo(() => {
    const map = new Map<string, Order[]>();
    orders.forEach(order => {
      if (order.isDeleted) return;
      const list = map.get(order.clientId) || [];
      list.push(order);
      map.set(order.clientId, list);
    });
    return map;
  }, [orders]);

  const today = new Date().toISOString().split('T')[0];
  const now = new Date().getTime();

  return (
    <div>
      {clients.length === 0 ? (
        <div className="text-center t-text-secondary mt-16 px-4">
          <UserIcon className="w-16 h-16 mx-auto t-text-muted opacity-50" />
          {searchQuery ? (
             <>
              <p className="mt-4 text-lg">Aucun résultat</p>
              <p className="text-sm">Aucun client ne correspond à "{searchQuery}".</p>
             </>
          ) : (
            <>
              <p className="mt-4 text-lg">Aucun client trouvé.</p>
              <p className="text-sm">Cliquez sur le bouton '+' pour en ajouter un.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="space-y-3 px-4">
          {clients.map(client => {
            const clientOrders = ordersByClient.get(client.id) || [];
            const hasUnpaid = clientOrders.some(o => !o.paid);
            const isNew = client.updatedAt && (now - new Date(client.updatedAt).getTime() < 24 * 60 * 60 * 1000);
            const hasLate = clientOrders.some(o => o.status !== 'delivered' && o.dueDate && o.dueDate < today);

            const lastMeasurement = client.measurements.length > 0
              ? [...client.measurements].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
              : null;
            
            let lastMeasurementText = 'Aucune mesure enregistrée';
            if (lastMeasurement && lastMeasurement.date) {
              const date = new Date(lastMeasurement.date);
              if (!isNaN(date.getTime())) {
                lastMeasurementText = `Dernière mesure le ${date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
              }
            }

            return (
              <li key={client.id}>
                <button
                  onClick={() => onSelectClient(client)}
                  className="w-full text-left p-4 t-card hover:border-brand-accent/40 active:scale-[0.98] transition-all flex items-center space-x-4 relative overflow-hidden group"
                >
                  {client.images && client.images.length > 0 ? (
                    <div className="w-12 h-12 shrink-0 rounded-full border border-gray-200 dark:border-slate-600 overflow-hidden bg-brand-light dark:bg-slate-700/50 group-hover:border-brand-accent/50 transition-colors">
                      <Image src={client.images[0]} alt={`Profil de ${client.name}`} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="p-3 bg-brand-light dark:bg-slate-700/50 rounded-full group-hover:bg-brand-accent/10 transition-colors">
                      <UserIcon className="w-6 h-6 text-brand-primary dark:text-brand-secondary" />
                    </div>
                  )}
                  <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-bold t-text-primary text-lg">{client.name}</p>
                        <div className="flex gap-1.5">
                          {hasUnpaid && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-bold rounded-full border border-red-200 dark:border-red-800/50 shadow-sm animate-pulse">
                              🔴 SOLDE
                            </span>
                          )}
                          {hasLate && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-bold rounded-full border border-amber-200 dark:border-amber-800/50 shadow-sm">
                              🟡 RETARD
                            </span>
                          )}
                          {isNew && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 text-[10px] font-bold rounded-full border border-indigo-200 dark:border-indigo-800/50 shadow-sm">
                              ⚡ NOUVEAU
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm t-text-secondary">{client.gender} - {client.contact || 'Aucun contact'}</p>
                      <p className="text-xs t-text-muted mt-1">{lastMeasurementText}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};